/**
 * Transport Team — unified app (organisation transport + marketplace driver booking).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { supabase, supabaseConfigured } from './supabase';
import { AuthContext, useAuth } from './authContext';
import { fetchProfile, isDeactivatedTeamDriver, onAuthStateChange, signOutSafely } from './lib/auth';
import { homePathForProfile } from './lib/homePath';
import { isPasswordResetPage } from './lib/passwordReset';

import Login from './pages/Login';
import BookDriver from './pages/BookDriver';
import OrgRegister from './pages/OrgRegister';
import Register from './pages/Register';
import AdminDashboard from './pages/AdminDashboard';
import DriverPortal from './pages/DriverPortal';
import DriverPending from './pages/DriverPending';
import PassengerPortal from './pages/PassengerPortal';
import MarketplaceLogin from './pages/MarketplaceLogin';
import MarketplaceRegister from './pages/MarketplaceRegister';
import MarketplacePassengerDash from './pages/MarketplacePassengerDash';
import MarketplaceDriverApply from './pages/MarketplaceDriverApply';
import MarketplaceDriverDash from './pages/MarketplaceDriverDash';
import MarketplaceAdminDash from './pages/MarketplaceAdminDash';
import SuperAdminDash from './pages/SuperAdminDash';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import HowItWorksGuide from './components/HowItWorksGuide';
import ConfigGuard from './components/ConfigGuard';

const shellStyle = {
  minHeight: '100vh',
  background: '#0f1419',
  color: '#e8eaed',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
};

function Spinner() {
  return (
    <div style={shellStyle}>
      <p style={{ color: '#9aa0a6', margin: 0 }}>Loading…</p>
    </div>
  );
}

function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileSyncing, setProfileSyncing] = useState(false);

  const syncProfile = useCallback(async (s) => {
    if (!s?.user) {
      setProfile(null);
      setProfileSyncing(false);
      return;
    }
    if (isPasswordResetPage()) {
      return;
    }
    setProfileSyncing(true);
    try {
      const { profile: p, error: profileErr } = await fetchProfile(s.user.id);
      if (profileErr) {
        setProfile(null);
        return;
      }
      if (isDeactivatedTeamDriver(p)) {
        await signOutSafely();
        setSession(null);
        setProfile(null);
        return;
      }
      if (p?.profile_scope === 'team' && p.org_active === false) {
        await signOutSafely();
        setSession(null);
        setProfile(null);
        return;
      }
      setProfile(p);
    } finally {
      setProfileSyncing(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!supabaseConfigured) return;
    const { data: { session: s } } = await supabase.auth.getSession();
    setSession(s ?? null);
    await syncProfile(s);
  }, [syncProfile]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    const unsub = onAuthStateChange((event, s) => {
      setSession(s ?? null);
      if (event === 'PASSWORD_RECOVERY' || isPasswordResetPage()) {
        return;
      }
      if (!s?.user) {
        setProfile(null);
        setProfileSyncing(false);
        return;
      }
      window.setTimeout(() => {
        void syncProfile(s);
      }, 0);
    });
    return unsub;
  }, [refresh, syncProfile]);

  const signOutUser = useCallback(async () => {
    await signOutSafely();
    setSession(null);
    setProfile(null);
  }, []);

  const value = useMemo(
    () => ({ session, profile, loading, profileSyncing, refresh, signOutUser }),
    [session, profile, loading, profileSyncing, refresh, signOutUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function RequireAuth({ children, scope }) {
  const { session, profile, loading, profileSyncing } = useAuth();
  const location = useLocation();

  if (loading || (session && profileSyncing && !profile)) return <Spinner />;
  if (!session || !profile) {
    const login = scope === 'marketplace' ? '/marketplace/login' : '/login';
    return <Navigate to={login} replace state={{ from: location.pathname }} />;
  }
  if (scope === 'team' && profile.profile_scope !== 'team') {
    return <Navigate to={homePathForProfile(profile)} replace />;
  }
  if (scope === 'marketplace' && profile.profile_scope !== 'marketplace') {
    return <Navigate to={homePathForProfile(profile)} replace />;
  }
  return children;
}

function RequireTeamRole({ role, children }) {
  const { profile, loading, session, profileSyncing } = useAuth();
  const location = useLocation();

  if (loading || (session && profileSyncing && !profile)) return <Spinner />;
  if (!profile || profile.profile_scope !== 'team') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (profile.role !== role) {
    return <Navigate to={homePathForProfile(profile)} replace />;
  }
  if (role === 'driver' && profile.approval_status !== 'approved') {
    return <Navigate to="/driver/pending" replace />;
  }
  if (role === 'driver' && profile.active === false) {
    return <Navigate to="/login" replace state={{ deactivated: true }} />;
  }
  return children;
}

function RequireTeamDriverPending({ children }) {
  const { profile, loading, session, profileSyncing } = useAuth();

  if (loading || (session && profileSyncing && !profile)) return <Spinner />;
  if (!profile || profile.profile_scope !== 'team' || profile.role !== 'driver') {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RequireMarketplaceApprovedDriver({ children }) {
  const { profile, loading, session, profileSyncing } = useAuth();

  if (loading || (session && profileSyncing && !profile)) return <Spinner />;
  if (!profile || profile.profile_scope !== 'marketplace' || profile.role !== 'driver') {
    return <Navigate to="/marketplace/login" replace />;
  }
  if (profile.marketplace_driver_status !== 'approved') {
    return <Navigate to="/marketplace/driver/apply" replace />;
  }
  return children;
}

function RequireSuperAdmin({ children }) {
  const { profile, loading, session, profileSyncing } = useAuth();
  const location = useLocation();

  if (loading || (session && profileSyncing && !profile)) return <Spinner />;
  if (!session || !profile) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!profile.super_admin) {
    return <Navigate to={homePathForProfile(profile)} replace state={{ superAdminDenied: true }} />;
  }
  return children;
}

function RequireMarketplaceRole({ role, children }) {
  const { profile, loading, session, profileSyncing } = useAuth();

  if (loading || (session && profileSyncing && !profile)) return <Spinner />;
  if (!profile || profile.profile_scope !== 'marketplace') {
    return <Navigate to="/marketplace/login" replace />;
  }
  if (role === 'admin') {
    if (!profile.marketplace_admin) {
      return <Navigate to={homePathForProfile(profile)} replace />;
    }
    return children;
  }
  if (profile.marketplace_admin || profile.role !== role) {
    return <Navigate to={homePathForProfile(profile)} replace />;
  }
  return children;
}

function HomeRoute() {
  const { session, profile, loading } = useAuth();
  if (loading) return <Spinner />;
  if (session && profile) {
    return <Navigate to={homePathForProfile(profile)} replace />;
  }
  return <Login />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ConfigGuard>
        <AuthProvider>
          <HowItWorksGuide />
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/book" element={<BookDriver />} />
            <Route path="/org/register" element={<OrgRegister />} />

            <Route path="/login" element={<HomeRoute />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:scopeParam" element={<ResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register/:slug" element={<Register />} />

            <Route
              path="/admin"
              element={
                <RequireAuth scope="team">
                  <RequireTeamRole role="admin">
                    <AdminDashboard />
                  </RequireTeamRole>
                </RequireAuth>
              }
            />
            <Route
              path="/driver/pending"
              element={
                <RequireAuth scope="team">
                  <RequireTeamDriverPending>
                    <DriverPending />
                  </RequireTeamDriverPending>
                </RequireAuth>
              }
            />
            <Route
              path="/driver"
              element={
                <RequireAuth scope="team">
                  <RequireTeamRole role="driver">
                    <DriverPortal />
                  </RequireTeamRole>
                </RequireAuth>
              }
            />
            <Route
              path="/passenger"
              element={
                <RequireAuth scope="team">
                  <RequireTeamRole role="passenger">
                    <PassengerPortal />
                  </RequireTeamRole>
                </RequireAuth>
              }
            />

            <Route
              path="/super-admin"
              element={
                <RequireSuperAdmin>
                  <SuperAdminDash />
                </RequireSuperAdmin>
              }
            />

            <Route path="/marketplace/login" element={<MarketplaceLogin />} />
            <Route path="/marketplace/forgot-password" element={<Navigate to="/forgot-password?scope=marketplace" replace />} />
            <Route path="/marketplace/register" element={<MarketplaceRegister />} />
            <Route
              path="/marketplace/passenger"
              element={
                <RequireAuth scope="marketplace">
                  <RequireMarketplaceRole role="passenger">
                    <MarketplacePassengerDash />
                  </RequireMarketplaceRole>
                </RequireAuth>
              }
            />
            <Route
              path="/marketplace/driver"
              element={
                <RequireAuth scope="marketplace">
                  <RequireMarketplaceApprovedDriver>
                    <MarketplaceDriverDash />
                  </RequireMarketplaceApprovedDriver>
                </RequireAuth>
              }
            />
            <Route
              path="/marketplace/driver/apply"
              element={
                <RequireAuth scope="marketplace">
                  <RequireMarketplaceRole role="driver">
                    <MarketplaceDriverApply />
                  </RequireMarketplaceRole>
                </RequireAuth>
              }
            />
            <Route
              path="/marketplace/admin"
              element={
                <RequireAuth scope="marketplace">
                  <RequireMarketplaceRole role="admin">
                    <MarketplaceAdminDash />
                  </RequireMarketplaceRole>
                </RequireAuth>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ConfigGuard>
    </BrowserRouter>
  );
}
